import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { head, isNil, defaultTo, length } from 'ramda'
import { useSelector } from 'hooks/redux'
import { useValidate, useValidators } from 'hooks/validation'
import { isArray, isNumber } from 'utils/types'

import { getInErrorState } from 'selectors/validation'

import debounceFn from 'utils/debounce'

import InputLabel from '../InputLabel'
import withStyle, {
  StyledFieldWrapper,
  StyledErrorMessage,
  StyledFieldFooter,
  StyledCharsRemaining,
} from './Field.style'

/* eslint-disable react-hooks/exhaustive-deps */
const Field = ({
  value,
  hasSelectedSection,
  onChange,
  onMouseDown,
  onBlur,
  label,
  hint,
  tooltip,
  tooltipAutoWidth,
  isSVGTooltip,
  rules,
  render,
  className,
  name,
  mask,
  placeholder,
  autoFocus,
  type: inputType,
  disabled,
  icon,
  hideErrorWhenDefault,
  debounce,
  'data-testid': dataTestId,
  wrapperDataTestId,
  getPopupContainer,
}) => {
  const debounceOnChange = useMemo(
    () => (debounce ? debounceFn(onChange, 100) : onChange),
    [debounce, onChange]
  )
  // TODO: find better solution (doesn't account for different error states)
  // for example save/send to test for features
  const inErrorState = useSelector(getInErrorState)
  const [isDirty, setIsDirty] = useState(false)
  const [forceShowErrors, setForceShowErrors] = useState(inErrorState)
  const [showError, setShowError] = useState(false)
  const [inititalOrblurredWhenInvalid, setInititalOrBlurredWhenInvalid] =
    useState(false)

  const containingEl = useRef()

  const [isValid, errors] = useValidate(value, rules)

  const {
    required,
    max,
    length: exactLength,
    optionalLength,
    type,
  } = useValidators(rules)

  const isNumberInput = type === 'number' || inputType === 'number'

  const counterMax = max || exactLength || optionalLength
  const charsRemaining =
    type === 'string' &&
    !isNil(counterMax) &&
    counterMax - defaultTo(0, length(value))

  // need to show errors if component mounts in an invalid state
  const setHasInitialErrors = useCallback(() => {
    const showInitialError = !isValid && value !== '' && !isNil(value)
    setInititalOrBlurredWhenInvalid(showInitialError)
  }, [isValid, setInititalOrBlurredWhenInvalid, value])

  useEffect(() => {
    setHasInitialErrors()
  }, [])

  useEffect(() => {
    if (
      !isDirty &&
      errors &&
      errors.includes('required') &&
      length(value) === 0
    ) {
      setShowError(false)
    } else {
      const showErrors =
        !disabled &&
        (errors !== null ||
          length(errors) ||
          ((inititalOrblurredWhenInvalid || forceShowErrors) && !isValid))

      if (hideErrorWhenDefault !== true) setShowError(showErrors)
    }
  }, [
    disabled,
    errors,
    inititalOrblurredWhenInvalid,
    forceShowErrors,
    isValid,
    value,
    isDirty,
    hideErrorWhenDefault,
  ])

  const handleChange = (event) => {
    // select doesn't have an 'event'
    const eventValue = event?.target ? event.target.value : event

    // default 'empty' value for numbers is `null`
    const castingFn = (v) => {
      // need to check inputType when the type property is 'mixed' which defaults to string (see yup documentation)
      // this is to account for an input schmea types of 'mixed' that can either be numbers or strings
      // may need to add a check for boolean if we ever encounter an input that could be both (unlikely)
      if (eventValue === '' && isNumberInput) return null
      // this is what yup uses to cast a number, so just manually do it to get around the 'mixed' problem stated above
      if (inputType === 'number') return parseFloat(v)

      return rules.cast(eventValue)
    }

    const castEventValue = castingFn(eventValue)

    // don't debounce selects
    if (event?.target) {
      debounceOnChange(castEventValue)
    } else {
      setIsDirty(true)
      onChange(castEventValue)
    }
  }

  const handleBlur = (event) => {
    if (!event) {
      setInititalOrBlurredWhenInvalid(!isValid)
    }
    setIsDirty(true)
    onBlur(event)
  }

  useEffect(() => {
    if (inErrorState) {
      setForceShowErrors(true)
    }
  }, [inErrorState])

  const displayError = isArray(errors) ? head(errors) : ''
  return (
    <StyledFieldWrapper
      className={className}
      errorState={showError}
      ref={containingEl}
      data-testid={wrapperDataTestId}
    >
      {label && (
        <InputLabel
          htmlFor={name}
          icon={icon}
          label={label}
          hint={hint}
          tooltip={tooltip}
          tooltipAutoWidth={tooltipAutoWidth}
          isSVGTooltip={isSVGTooltip}
          required={required}
          vertical
        />
      )}
      {render({
        value: defaultTo(undefined, value),
        name,
        onChange: handleChange,
        onBlur: handleBlur,
        onMouseDown,
        mask,
        placeholder,
        autoFocus,
        disabled,
        getPopupContainer: getPopupContainer || (() => containingEl.current),
        maxLength:
          type === 'string' && !isNil(counterMax) ? counterMax : undefined,
        'data-testid': dataTestId,
      })}

      <StyledFieldFooter>
        {showError && <StyledErrorMessage>{displayError}</StyledErrorMessage>}
        {isNumber(charsRemaining) && !disabled && (
          <StyledCharsRemaining>{charsRemaining}</StyledCharsRemaining>
        )}
      </StyledFieldFooter>
    </StyledFieldWrapper>
  )
}

Field.defaultProps = {
  onChange: () => {},
  onBlur: () => {},
  size: 'default',
  placeholder: '',
  hideErrorWhenDefault: false,
  debounce: false,
}

Field.propTypes = {
  className: PropTypes.string,
  icon: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]),
  hasSelectedSection: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onMouseDown: PropTypes.func,
  label: PropTypes.string,
  hint: PropTypes.string,
  tooltip: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.node,
    PropTypes.element,
    PropTypes.func,
  ]),
  tooltipAutoWidth: PropTypes.bool,
  isSVGTooltip: PropTypes.bool,
  name: PropTypes.string.isRequired,
  rules: PropTypes.object.isRequired,
  children: PropTypes.element,
  forceValidation: PropTypes.bool,
  render: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  /** string value that determines element and related text size */
  size: PropTypes.oneOf(['small', 'default', 'large']),
  mask: PropTypes.string,
  autoFocus: PropTypes.bool,
  type: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
  hideErrorWhenDefault: PropTypes.bool,
  debounce: PropTypes.bool,
  'data-testid': PropTypes.string,
  wrapperDataTestId: PropTypes.string,
  getPopupContainer: PropTypes.func,
}

export default withStyle(Field)
